import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Users,
  Calendar,
  CreditCard,
  Settings,
  LogOut,
  ClipboardList,
  Heart,
  Clock,
  BarChart2,
  Tag,
} from 'lucide-react';
import { brandConfig } from '../../brand';
import { useAuth } from '../../lib/AuthProvider';

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  to: string;
  active: boolean;
  collapsed: boolean;
  onNavigate?: () => void;
}

function SidebarItem({ icon, label, to, active, collapsed, onNavigate }: SidebarItemProps) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={`flex items-center px-4 py-2 rounded transition-colors hover:bg-slate-800 ${active ? 'bg-slate-800 text-white' : 'text-slate-300'} ${collapsed ? 'lg:justify-center' : ''}`}
    >
      {icon}
      {/* On mobile drawer: always show label. On desktop: respect collapsed */}
      <span className={`ml-3 ${collapsed ? 'lg:hidden' : ''}`}>{label}</span>
    </Link>
  );
}

const navItems = [
  { label: 'Dashboard', to: '/admin', icon: <Home /> },
  { label: 'Members', to: '/admin/members', icon: <Users /> },
  { label: 'Name Badges', to: '/admin/badges', icon: <Tag /> },
  { label: 'Events', to: '/admin/events', icon: <Calendar /> },
  { label: 'Attendance', to: '/admin/attendance', icon: <ClipboardList /> },
  { label: 'Volunteer Hours', to: '/admin/volunteer-hours', icon: <Clock /> },
  { label: 'Interests', to: '/admin/interests', icon: <Heart /> },
  { label: 'Payments', to: '/admin/payments', icon: <CreditCard /> },
  { label: 'Reports', to: '/admin/reports', icon: <BarChart2 /> },
  { label: 'Functions', to: '/admin/functions', icon: <Settings /> },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signOut } = useAuth();

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await signOut();
      onClose();
      navigate('/login');
    } catch (err) {
      setError('Failed to log out. Please try again.');
    }
  };

  // On mobile, always show expanded (w-64); on desktop, respect collapsed state
  const sidebarWidth = collapsed ? 'lg:w-20' : 'lg:w-64';

  return (
    <>
      {/* Backdrop overlay — mobile only */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-screen w-64 ${sidebarWidth} bg-slate-900 flex flex-col z-50 transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:transition-all lg:duration-200`}
      >
        <div className="flex items-center justify-center h-16 border-b border-slate-800">
          <span className={`text-white font-semibold ${collapsed ? 'lg:text-sm text-lg' : 'text-lg'}`}>
            {!collapsed ? brandConfig.name : (
              <>
                <span className="lg:inline hidden">{brandConfig.name.split(' ')[0]}</span>
                <span className="lg:hidden">{brandConfig.name}</span>
              </>
            )}
          </span>
        </div>
        <nav className="flex-1 overflow-y-auto min-h-0 py-4 space-y-1">
          {navItems.map((item) => (
            <SidebarItem
              key={item.to}
              icon={item.icon}
              label={item.label}
              to={item.to}
              active={location.pathname === item.to}
              collapsed={collapsed}
              onNavigate={onClose}
            />
          ))}
        </nav>
        <div className="p-2 border-t border-slate-800">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="w-full hidden lg:flex items-center justify-center text-slate-400 hover:text-white py-2"
          >
            {collapsed ? (
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            ) : (
              <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
            )}
            {!collapsed && <span className="ml-2 text-sm">Collapse</span>}
          </button>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center justify-center text-slate-400 hover:text-white py-2 mt-2 ${collapsed ? 'lg:justify-center' : ''}`}
          >
            <LogOut className="h-6 w-6" />
            {/* On mobile drawer: always show label. On desktop: respect collapsed */}
            <span className={`ml-3 ${collapsed ? 'lg:hidden' : ''}`}>Logout</span>
          </button>
          {error && (
            <div className="text-red-500 text-xs mt-2 text-center">{error}</div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar; 
